import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { InputMultiSelect } from '@/design_system/InputMultiSelect/InputMultiSelect';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Title from '@/design_system/Typography/Title';
import { useActionTypesOrganization } from '@/models/actionType';
import { useBrands } from '@/models/brand';
import { useCreateDefectType, useUpdateDefectType } from '@/models/defectType';
import { useCurrentOrganization } from '@/services/auth';
import { I18nWording, SUPPORTED_LOCALES, useDatabaseTranslation } from '@/services/i18n';
import { isMissingTranslation } from '@/utils/isMissingTranslation';
import { trimI18nWording } from '@/utils/trimI18nWording';
import useViewPort from '@/utils/useViewport';

import { IsQuantifiableRadio } from '../shared/IsQuantifiableRadio';
import { NameInputs } from '../shared/NameInputs';
import { ProductAttributeValuesMultiSelects } from '../shared/ProductAttributeValuesMultiSelects';

import { DefectBrandsInputs } from './DefectBrandsInputs';
import { DefectLevelValues } from './DefectLevelValues';
import type { DefectBrandValue, DefectType, IDefectLevelValue } from './DefectsTable';

export const DefectDrawer = ({
  defectType,
  onClose,
  isOpen,
}: {
  defectType: DefectType | null;
  onClose: () => void;
  isOpen: boolean;
}) => {
  return (
    <Drawer isOpen={isOpen} onOpenChange={onClose}>
      <DefectDrawerContent defectType={defectType} onClose={onClose} />
    </Drawer>
  );
};

const DefectDrawerContent = ({
  defectType,
  onClose,
}: {
  defectType: DefectType | null;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const showToast = useShowToast();
  const { _db } = useDatabaseTranslation();
  const [currentOrganization] = useCurrentOrganization();

  const { mutateAsync: updateDefectType, isPending: isUpdateDefectTypePending } =
    useUpdateDefectType();
  const { mutateAsync: createDefectType, isPending: isCreateDefectTypePending } =
    useCreateDefectType();

  const { data } = useBrands({
    organizationId: currentOrganization?.id,
    limit: 500,
    offset: 0,
  });

  const [name, setName] = useState(
    Object.fromEntries(
      SUPPORTED_LOCALES.map(({ id }) => [id, defectType?.name[id] ?? ''])
    ) as I18nWording
  );

  const [isQuantifiable, setIsQuantifiable] = useState(defectType?.isQuantifiable ?? true);
  const [productAttributeValues, setProductAttributeValues] = useState(
    defectType?.productAttributeValues ?? []
  );
  const [brands, setBrands] = useState<DefectBrandValue[]>(
    defectType?.brandToDefectTypeOrganizations ?? []
  );

  const [actionIds, setActionIds] = useState(
    defectType?.actionTypeToDefectTypes.map(
      ({ actionTypeOrganization }) => actionTypeOrganization.id
    ) ?? []
  );
  const [defectLevelValue, setDefectLevelValue] = useState<IDefectLevelValue | null>(
    defectType?.defectLevelValue ?? null
  );

  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const hasNameError = isMissingTranslation(name);
  const hasBrandsError = brands.some((brand) => !brand.brandId || brand.brandId === 'tmp');
  const hasError = hasNameError || hasBrandsError;

  const brandOptions = data?.brands ?? [];

  const onSave = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      const body = {
        name: trimI18nWording(name),
        isQuantifiable,
        productAttributeValues: productAttributeValues.map(({ id }) => id),
        actionTypeOrganizations: actionIds.map((id) => ({ id })),
        defectLevelValueId: defectLevelValue?.id ?? null,
        brands,
      };

      if (defectType) {
        await updateDefectType({
          defectTypeId: defectType.id,
          ...body,
        });
      } else {
        await createDefectType(body);
      }

      onClose();

      const defectTypeName = _db(name);

      showToast({
        text: (
          <Trans id="settings.defects.drawer.update-success">
            Defect &quot;{defectTypeName}&quot; successfully saved
          </Trans>
        ),
      });
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const { data: { actionTypes: actionOptions = [] } = {} } = useActionTypesOrganization();

  return (
    <>
      <DrawerHeader>
        <Title size="300">
          {defectType ? (
            <Trans id="settings.defects.drawer.title">Update defect</Trans>
          ) : (
            <Trans id="settings.defects.drawer.title-create">Create defect</Trans>
          )}
        </Title>
      </DrawerHeader>

      <DrawerBody>
        <Stack padding={isMobile ? '1rem' : '2rem'} gap="1rem">
          <NameInputs name={name} setName={setName} showErrors={showErrors} />

          <IsQuantifiableRadio
            isQuantifiable={isQuantifiable}
            setIsQuantifiable={setIsQuantifiable}
          />

          <DefectLevelValues
            defectLevelValueForDefectType={defectLevelValue}
            setDefectLevelValueForDefectType={setDefectLevelValue}
          />

          <ProductAttributeValuesMultiSelects
            productAttributeValues={productAttributeValues}
            setProductAttributeValues={setProductAttributeValues}
          />

          <DefectBrandsInputs
            brands={brands}
            setBrands={setBrands}
            brandOptions={brandOptions}
            isDisabled={!brandOptions.length}
            showErrors={hasBrandsError && showErrors}
          />

          <InputMultiSelect
            variant="select"
            label={<Trans id="settings.defects.drawer.actions">Linked actions</Trans>}
            placeholder={t({
              id: 'settings.defects.drawer.actions',
              message: 'Linked actions',
            })}
            value={actionOptions.filter((actionOption) => actionIds.includes(actionOption.id))}
            options={actionOptions}
            getOptionValue={(actionOption) => actionOption.id}
            getOptionLabel={(actionOption) => _db(actionOption.name)}
            onChange={(actionOptions) =>
              setActionIds(actionOptions.map((actionOption) => actionOption.id))
            }
            onRemoveValue={(optionActionId) =>
              setActionIds(actionIds.filter((actionId) => actionId !== optionActionId))
            }
            isDisabled={!actionOptions.length}
          />
        </Stack>
      </DrawerBody>

      <Footer>
        {(apiError || (showErrors && hasError)) && (
          <FooterError>
            {!apiError && showErrors && hasError ? (
              <Trans id="settings.defects.drawer.error">Please fill the missing information</Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}

        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onClose}>
            <Trans id="settings.defects.drawer.cancel">Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            size="medium"
            onPress={onSave}
            isLoading={isUpdateDefectTypePending || isCreateDefectTypePending}
          >
            <Trans id="settings.defects.drawer.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
